import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const PREDEFINED_SCENARIOS = [
  {
    id: 'overtime_error',
    name: 'Overtime Calculation Error',
    triggerType: 'overtime_rate_violation',
    severity: 'high',
    description: 'Overtime rate calculated at 1.0x instead of 1.5x',
    estimatedCost: 500,
    regulationReference: 'Employment Act Section 45'
  },
  {
    id: 'minimum_wage',
    name: 'Minimum Wage Violation',
    triggerType: 'minimum_wage_breach',
    severity: 'critical',
    description: 'Employee paid below PNG minimum wage of K3.50/hour',
    estimatedCost: 1200,
    regulationReference: 'Minimum Wages Board Determination'
  },
  {
    id: 'nasfund_error',
    name: 'NASFUND Contribution Error',
    triggerType: 'nasfund_calculation_error',
    severity: 'high',
    description: 'NASFUND contribution calculated at incorrect rate',
    estimatedCost: 800,
    regulationReference: 'NASFUND Act'
  },
  {
    id: 'leave_breach',
    name: 'Leave Policy Breach',
    triggerType: 'leave_entitlement_breach',
    severity: 'medium',
    description: 'Annual leave entitlement not accrued correctly',
    estimatedCost: 300,
    regulationReference: 'Employment Act Section 52'
  },
  {
    id: 'working_hours',
    name: 'Working Hours Violation',
    triggerType: 'working_hours_violation',
    severity: 'high',
    description: 'Employee worked beyond maximum weekly hours',
    estimatedCost: 600,
    regulationReference: 'Employment Act Section 44'
  },
  {
    id: 'payment_delay',
    name: 'Payment Delay Violation',
    triggerType: 'payment_delay_violation',
    severity: 'critical',
    description: 'Salary payment delayed beyond statutory deadline',
    estimatedCost: 1000,
    regulationReference: 'Employment Act Section 48'
  }
];

const ViolationTriggerInterface = ({
  employees,
  selectedClients,
  onTriggerViolation,
  onBulkTest,
  activeTest
}) => {
  const [mode, setMode] = useState('predefined'); // 'predefined' or 'custom'
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [customConfig, setCustomConfig] = useState({
    triggerType: 'minimum_wage_breach',
    severity: 'high',
    description: '',
    department: '',
    estimatedCost: 0,
    affectedEmployeeIds: [],
    regulationReference: ''
  });
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  const handleTriggerPredefined = () => {
    if (!selectedScenario) return;

    const scenario = PREDEFINED_SCENARIOS?.find(s => s?.id === selectedScenario);
    if (!scenario) return;

    const config = {
      ...scenario,
      affectedEmployeeIds: selectedEmployees,
      department: employees?.find(e => selectedEmployees?.includes(e?.id))?.department || 'General'
    };

    onTriggerViolation(config);
  };

  const handleTriggerCustom = () => {
    if (!customConfig?.description) return;

    onTriggerViolation({
      ...customConfig,
      affectedEmployeeIds: selectedEmployees
    });
  };

  const handleBulkTest = () => {
    const configs = PREDEFINED_SCENARIOS?.map(scenario => ({
      ...scenario,
      affectedEmployeeIds: selectedEmployees?.slice(0, 2), // Use first 2 employees for each test
      department: employees?.[0]?.department || 'General'
    }));

    onBulkTest(configs);
  };

  const isDisabled = activeTest?.status === 'running' || selectedClients?.length === 0;

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border bg-muted/30">
        <h3 className="font-semibold text-foreground flex items-center gap-2">
          <Icon name="Zap" size={20} />
          Violation Trigger Interface
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          Select test scenario and trigger violations to verify notification delivery
        </p>
      </div>

      {/* Mode Selector */}
      <div className="p-4 border-b border-border">
        <div className="flex gap-2">
          <button
            onClick={() => setMode('predefined')}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              mode === 'predefined' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            <Icon name="List" size={16} className="inline mr-2" />
            Predefined Scenarios
          </button>
          <button
            onClick={() => setMode('custom')}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              mode === 'custom' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            <Icon name="Settings" size={16} className="inline mr-2" />
            Custom Builder
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {selectedClients?.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="AlertCircle" size={48} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">Please select at least one client to begin testing</p>
          </div>
        ) : (
          <>
            {mode === 'predefined' ? (
              <div className="space-y-4">
                {/* Scenario Selection */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Test Scenario
                  </label>
                  <select
                    value={selectedScenario || ''}
                    onChange={(e) => setSelectedScenario(e?.target?.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                    disabled={isDisabled}
                  >
                    <option value="">Select a scenario...</option>
                    {PREDEFINED_SCENARIOS?.map(scenario => (
                      <option key={scenario?.id} value={scenario?.id}>
                        {scenario?.name} ({scenario?.severity})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Scenario Details */}
                {selectedScenario && (
                  <div className="p-4 bg-muted/30 rounded-lg border border-border">
                    {(() => {
                      const scenario = PREDEFINED_SCENARIOS?.find(s => s?.id === selectedScenario);
                      return (
                        <>
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              scenario?.severity === 'critical' ? 'bg-red-100 text-red-700' :
                              scenario?.severity === 'high'? 'bg-orange-100 text-orange-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {scenario?.severity?.toUpperCase()}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {scenario?.triggerType}
                            </span>
                          </div>
                          <p className="text-sm text-foreground mb-2">{scenario?.description}</p>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span>Est. Cost: K{scenario?.estimatedCost}</span>
                            <span>{scenario?.regulationReference}</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Employee Selection */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Affected Employees (Optional)
                  </label>
                  <select
                    multiple
                    value={selectedEmployees}
                    onChange={(e) => setSelectedEmployees(
                      Array.from(e?.target?.selectedOptions, option => option?.value)
                    )}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground min-h-[100px]"
                    disabled={isDisabled}
                  >
                    {employees?.slice(0, 20)?.map(emp => (
                      <option key={emp?.id} value={emp?.id}>
                        {emp?.firstName} {emp?.lastName} - {emp?.department}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Hold Ctrl/Cmd to select multiple employees
                  </p>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <Button
                    onClick={handleTriggerPredefined}
                    disabled={!selectedScenario || isDisabled}
                    iconName="Play"
                    className="flex-1"
                  >
                    Trigger Test Violation
                  </Button>
                  <Button
                    onClick={handleBulkTest}
                    disabled={isDisabled}
                    variant="outline"
                    iconName="Layers"
                  >
                    Bulk Test All
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Custom Violation Builder */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Trigger Type
                    </label>
                    <select
                      value={customConfig?.triggerType}
                      onChange={(e) => setCustomConfig({ ...customConfig, triggerType: e?.target?.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                      disabled={isDisabled}
                    >
                      <option value="minimum_wage_breach">Minimum Wage Breach</option>
                      <option value="overtime_rate_violation">Overtime Rate Violation</option>
                      <option value="leave_entitlement_breach">Leave Entitlement Breach</option>
                      <option value="nasfund_calculation_error">NASFUND Calculation Error</option>
                      <option value="swt_calculation_error">SWT Calculation Error</option>
                      <option value="working_hours_violation">Working Hours Violation</option>
                      <option value="payment_delay_violation">Payment Delay Violation</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Severity Level
                    </label>
                    <select
                      value={customConfig?.severity}
                      onChange={(e) => setCustomConfig({ ...customConfig, severity: e?.target?.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                      disabled={isDisabled}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Description
                  </label>
                  <textarea
                    value={customConfig?.description}
                    onChange={(e) => setCustomConfig({ ...customConfig, description: e?.target?.value })}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                    rows={3}
                    placeholder="Describe the test violation scenario..."
                    disabled={isDisabled}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Department
                    </label>
                    <Input
                      value={customConfig?.department}
                      onChange={(e) => setCustomConfig({ ...customConfig, department: e?.target?.value })}
                      placeholder="e.g., Finance"
                      disabled={isDisabled}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Estimated Cost (PGK)
                    </label>
                    <Input
                      type="number"
                      value={customConfig?.estimatedCost}
                      onChange={(e) => setCustomConfig({ ...customConfig, estimatedCost: parseFloat(e?.target?.value) || 0 })}
                      placeholder="0.00"
                      disabled={isDisabled}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Regulation Reference
                  </label>
                  <Input
                    value={customConfig?.regulationReference}
                    onChange={(e) => setCustomConfig({ ...customConfig, regulationReference: e?.target?.value })}
                    placeholder="e.g., Employment Act Section 45"
                    disabled={isDisabled}
                  />
                </div>

                <Button
                  onClick={handleTriggerCustom}
                  disabled={!customConfig?.description || isDisabled}
                  iconName="Play"
                  className="w-full"
                >
                  Trigger Custom Violation
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ViolationTriggerInterface;